import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaEdit, FaTrash } from "react-icons/fa";
import { saveJsonLearnToBack, fetchJsonFromBack } from '../../../store/jsonSliceLearn';
import Sortable from "sortablejs";
import Title from "../../../components/Title";
import TextInput from "../../../components/TextInput";
import Button from "../../../components/Button";
import TextArea from "../../../components/TextArea";
import { deleteFile, uploadFile } from "../../../services/api/jsonService.ts";
import { updateJsonDataLearn } from "../../../store/jsonSliceLearn.js";

const CoursesManage = () => {
  const dispatch = useDispatch();
  const { chapterName, folderName: urlFolderName } = useParams();
  const { jsonData, loading: jsonLoading, error } = useSelector((state) => state.jsonLearn);

  const process = jsonData?.process || { courses: [] };
  const courses = Array.isArray(process.courses) ? process.courses : [];

  const [pendingEnv360, setPendingEnv360] = useState(null);

  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [courseData, setCourseData] = useState({
    courseID: "",
    courseTitle: "",
    courseDesc: "",
    courseRequiresCourseID: "",
    coursePath: "",
    courseImg: "",
    courseEnv360: "",
    courseEnv360Rot: "",
    coursePos: { x: 0, y: 0, z: 0 },
    courseRot: { x: 0, y: 0, z: 0 },
    courseAudioText: "",
    courseAudio: ""
  });
  // pathTopics contient les IDs sélectionnés dans l'ordre
  const [pathTopics, setPathTopics] = useState([]);
  const [file, setFile] = useState(null);
  const [previewCourseAudioUrl, setPreviewCourseAudioUrl] = useState(null);

  const topics = useSelector(state =>
    Array.isArray(state.jsonLearn?.jsonData?.process?.topics)
      ? state.jsonLearn.jsonData.process.topics.filter(t => t && typeof t.topicID !== 'undefined')
      : []
  );
  // Pour l'affichage à gauche :
  const availableTopics = topics.map(t => ({ id: t.topicID, title: t.topicTitle || 'Sans titre' }));
  // Clé calculée à partir des IDs disponibles : quand elle change, React démonte
  // complètement les conteneurs Sortable (cleanup useEffect = destroy), puis les
  // remonte proprement, ce qui élimine tout conflit entre Sortable et le DOM React.
  const sortableContainerKey = availableTopics.map(t => t.id).join('-') || 'empty';
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  // Supprime un topic du chemin du course (pathTopics)
  function handleRemoveTopicFromPath(topicId) {
    setPathTopics(prev => prev.filter(id => id !== topicId));
    setCourseData(prev => ({
      ...prev,
      coursePath: prev.coursePath
        ? prev.coursePath.split('.').filter(id => Number(id) !== topicId).join('.')
        : ''
    }));
  }
  // Met à jour le champ coursePath à chaque changement de pathTopics
  useEffect(() => {
    setCourseData(prev => ({
      ...prev,
      coursePath: pathTopics.length > 0 ? pathTopics.join('.') : ''
    }));
  }, [pathTopics]);

  // Chargement initial 
  useEffect(() => {
    if (urlFolderName) {
      // Nettoyer d'abord le .json, puis le _learn
      const cleanName = urlFolderName.replace(/\.json$/, '');
      const base = cleanName.replace(/_learn$/, '');
      const activeChapterName = chapterName || base.split('_')[0];
      const languageFolder = `${base}/learn`;
      const learnFilename = `${base}_learn.json`;
      const finalFolderPath = `${activeChapterName}/${languageFolder}`;

      dispatch(fetchJsonFromBack({
        folderName: finalFolderPath,
        filename: learnFilename
      }));
    }
  }, [urlFolderName, dispatch]);
  // On déplace la logique de calcul du chemin final hors du useEffect
  let folderPathFinal = '';
  if (urlFolderName) {
    const cleanName = urlFolderName.replace(/\.json$/, '');
    const base = cleanName.replace(/_learn$/, '');
    const activeChapterName = chapterName || base.split('_')[0];
    const languageFolder = `${base}/learn`;
    folderPathFinal = `${activeChapterName}/${languageFolder}`;
  }

  // Références pour stocker les instances Sortable afin de pouvoir les détruire
  const sortableLeftRef = useRef(null);
  const sortableRightRef = useRef(null);

  // Cette ligne permet de créer une instance Sortable pour chaque liste
  // de gauche et de droite, permettant ainsi de glisser-déposer les éléments.
  // On dépend de availableTopics.length et pathTopics.length pour recréer
  // les instances quand React reconstruit les listes DOM.
  useEffect(() => {
    if (!leftRef.current || !rightRef.current) return;

    // Détruire les instances précédentes avant que React ne reconstruit le DOM
    if (sortableLeftRef.current) {
      sortableLeftRef.current.destroy();
      sortableLeftRef.current = null;
    }
    if (sortableRightRef.current) {
      sortableRightRef.current.destroy();
      sortableRightRef.current = null;
    }

    sortableLeftRef.current = Sortable.create(leftRef.current, {
      group: {
        name: "shared",
        pull: "clone",
        put: false,
      },
      sort: false,
      animation: 150,
    });

    // Cette instance permet de glisser-déposer les éléments de la liste de droite
    sortableRightRef.current = Sortable.create(rightRef.current, {
      group: {
        name: "shared",
        pull: false,
        put: true,
      },
      animation: 150,
      // Gérer l'ajout d'éléments depuis la liste de gauche :
      // On remet immédiatement l'élément déplacé par Sortable dans son parent
      // pour que React reste maître du DOM, puis on met à jour le state.
      onAdd: function (evt) {
        // Annuler la manipulation DOM de Sortable de façon synchrone
        // AVANT que React ne commence son cycle de rendu
        if (evt.item && evt.item.parentNode) {
          evt.item.parentNode.removeChild(evt.item);
        }
        const topicId = Number(evt.item.dataset.topicid);
        if (!isNaN(topicId)) {
          setPathTopics((prev) => prev.includes(topicId) ? prev : [...prev, topicId]);
        }
      },
      // Gérer la réorganisation des éléments dans la liste de droite
      onUpdate: function (evt) {
        // Récupérer tous les éléments de la liste de droite dans leur nouvel ordre
        const newOrder = Array.from(rightRef.current.children).map(item => {
          return Number(item.dataset.topicid);
        }).filter(id => !isNaN(id));

        // Mettre à jour pathTopics avec le nouvel ordre
        setPathTopics(newOrder);
      },
    });

    return () => {
      if (sortableLeftRef.current) {
        sortableLeftRef.current.destroy();
        sortableLeftRef.current = null;
      }
      if (sortableRightRef.current) {
        sortableRightRef.current.destroy();
        sortableRightRef.current = null;
      }
    };
  // Les dépendances incluent sortableContainerKey : quand les topics changent,
  // la key du div change → React remonte les <ul> → useEffect cleanup détruit
  // l'ancien Sortable → useEffect recrée Sortable sur les nouveaux nœuds DOM.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortableContainerKey]);

  // Générer automatiquement un courseID si vide
  useEffect(() => {
    if (!courseData.courseID && editingIndex === null) {
      // Générer un nouvel ID basé sur les cours existants
      const existingIds = courses.map(c => parseInt(c.courseID) || 0);
      const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
      const newId = maxId + 1;

      setCourseData(prev => ({
        ...prev,
        courseID: newId.toString()
      }));
    }
  }, [courseData.courseID, editingIndex, courses]);

  // Live preview for course audio from selected file
  useEffect(() => {
    if (!file) {
      setPreviewCourseAudioUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewCourseAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleChange = (name, value) => {
    setCourseData((prev) => ({ ...prev, [name]: value }));
  };
  const handleDeleteFile = async ({
    fileName, // "audio", "img"
    params,
    ext, // "ogg", "mp3", "png"
    fieldName, // nom du champ à vider dans Contents
  }) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce fichier ?")) return;
    if (!fileName || !params) {
      console.log(`[DELETE] Suppression file : paramètres manquants`, { fileName, params });
      return;
    }
    try {
      const cleanName = urlFolderName.replace(/\.json$/, '');
      const base = cleanName.replace(/_learn$/, '');
      const activeChapterName = chapterName || base.split('_')[0];
      const languageFolder = `${base}/learn`;
      const finalFolderPath = `${activeChapterName}/${languageFolder}`;

      const data = {
        folder: finalFolderPath,
        suffix: fileName,
        defaultExt: ext,
        params,
      };
      await deleteFile(data);
      setCourseData(prev => ({
        ...prev,
        [fieldName]: ""
      }));
      setMessage({ type: "success", text: ` supprimé avec succès !` });
    } catch (err) {
      setMessage({ type: "error", text: `Erreur lors de la suppression du fichier.` });
    }
  };

  const handleUploadFile = async ({
    file,
    params,
    fieldName,      // ex: 'courseImg' ou 'courseAudio'
    suffix,         // ex: 'courseImg_' ou 'courseAudio_'
    defaultExt,     // ex: 'png' ou 'ogg'
  }) => {
    if (!file || !params) {
      console.log('[UPLOAD] Paramètres manquants', { file, params });
      setMessage && setMessage({ type: "error", text: "Paramètres manquants pour l'upload." });
      return;
    }
    try {
      const cleanName = urlFolderName.replace(/\.json$/, '');
      const base = cleanName.replace(/_learn$/, '');
      const activeChapterName = chapterName || base.split('_')[0];
      const languageFolder = `${base}/learn`;
      const finalFolderPath = `${activeChapterName}/${languageFolder}`;

      const data = {
        file,
        folder: finalFolderPath,
        suffix,
        defaultExt,
        params
      };
      const res = await uploadFile(data);
      setCourseData && setCourseData(prev => ({
        ...prev,
        [fieldName]: `course-${params[0]}_${suffix}${defaultExt}`
      }));
      setFile && setFile(null);
      setMessage && setMessage({ type: "success", text: "Fichier enregistré avec succès !" });
    } catch (err) {
      setMessage && setMessage({ type: "error", text: "Erreur lors de l'upload du fichier." });
    }
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    const selectedCourse = courses[index];
    setCourseData(selectedCourse);
    // On split sur les points pour récupérer les IDs (format "11.12.13")
    setPathTopics(selectedCourse.coursePath ? selectedCourse.coursePath.split('.').map(Number).filter(Boolean) : []);
  };



  // Correction de handleDelete 
  const handleDelete = async (index) => {
    if (
      window.confirm(
        `Êtes-vous sûr de vouloir supprimer l'évaluation "${courses[index].courseTitle}" ?`
      )
    ) {
      try {
        const newCourses = courses.filter((_, i) => i !== index);
        const updatedData = {
          usage: jsonData?.usage || "Fichier des questionnaires liés aux procédures (process) d'iXiWORK.",
          settings: {
            contentVersion: jsonData?.settings?.contentVersion || 1.0,
            ownerName: jsonData?.settings?.ownerName || '',
            ownerIdentifier: jsonData?.settings?.ownerIdentifier || '',
            appVersion: jsonData?.settings?.appVersion || "",
            chapter: jsonData?.settings?.chapter || "",
            calibrateAfterStageID: jsonData?.settings?.calibrateAfterStageID || 0,
            unit: jsonData?.settings?.unit || "mm",
            langCode: jsonData?.settings?.langCode || ""
          },
          process: {
            topics: Array.isArray(jsonData?.process?.topics) ? jsonData.process.topics : [],
            courses: newCourses
          }
        };

        dispatch(updateJsonDataLearn(updatedData));

        const cleanName = urlFolderName.replace(/\.json$/, '');
        const base = cleanName.replace(/_learn$/, '');
        const activeChapterName = chapterName || base.split('_')[0];
        const languageFolder = `${base}/learn`;
        const learnFilename = `${base}_learn.json`;

        await dispatch(saveJsonLearnToBack({
          updatedData,
          chapterName: activeChapterName,
          languageFolder,
          filename: learnFilename
        }));

        setMessage({ type: "success", text: "Évaluation supprimée avec succès." });
      } catch (error) {
        setMessage({ type: "error", text: error.message });
      }


    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Créer une copie des données du cours pour les modifications
    let updatedCourseData = { ...courseData };

    // Upload de l'environnement 360° si présent
    if (pendingEnv360) {
      try {
        // Utilise folderPathFinal calculé dans le scope du composant
        const data = {
          file: pendingEnv360,
          folder: folderPathFinal,
          suffix: "env",
          //fieldName: "courseEnv360",
          defaultExt: ".jpg",
          params: [courseData.courseID || "1"]
        };
        console.log('[UPLOAD] Data envoyé à uploadFile:', data);
        const res = await uploadFile(data);
        console.log('[UPLOAD] Réponse uploadFile:', res);
        const fileName = `course-${courseData.courseID || "1"}_env.jpg`;
        updatedCourseData.courseEnv360 = fileName;
      } catch (err) {
        setMessage({ type: "error", text: "Erreur lors de l'upload de l'environnement 360°." });
        setLoading(false);
        return;
      }
    }

    try {
      const updatedData = {
        usage: jsonData?.usage || "Fichier des questionnaires liés aux procédures (process) d'iXiWORK.",
        settings: {
          contentVersion: jsonData?.settings?.contentVersion || 1.0,
          ownerName: jsonData?.settings?.ownerName || '',
          ownerIdentifier: jsonData?.settings?.ownerIdentifier || '',
          appVersion: jsonData?.settings?.appVersion || "",
          chapter: jsonData?.settings?.chapter || "",
          calibrateAfterStageID: jsonData?.settings?.calibrateAfterStageID || 0,
          unit: jsonData?.settings?.unit || "mm",
          langCode: jsonData?.settings?.langCode || ""
        },
        process: {
          topics: Array.isArray(jsonData?.process?.topics) ? jsonData.process.topics : [],
          courses: Array.isArray(jsonData?.process?.courses) ? [...jsonData.process.courses] : []
        }
      };

      if (editingIndex !== null && updatedData.process.courses[editingIndex]) {
        updatedData.process.courses[editingIndex] = { ...updatedCourseData };
      } else {
        updatedData.process.courses.push({ ...updatedCourseData });
      }

      dispatch(updateJsonDataLearn(updatedData));

      // Construction des bons paramètres pour le chemin learn
      const cleanName = urlFolderName.replace(/\.json$/, '');
      const base = cleanName.replace(/_learn$/, '');
      const activeChapterName = chapterName || base.split('_')[0];
      const languageFolder = `${base}/learn`;
      const learnFilename = `${base}_learn.json`;

      await dispatch(saveJsonLearnToBack({
        updatedData,
        chapterName: activeChapterName,
        languageFolder,
        filename: learnFilename
      }));

      // Mettre à jour l'état local après sauvegarde réussie
      setCourseData(updatedCourseData);

      setMessage({ type: "success", text: editingIndex !== null ? "Cours modifié." : "Cours ajouté." });

      // Reset pour nouveau cours
      setCourseData({
        courseID: "",
        courseTitle: "",
        courseDesc: "",
        courseRequiresCourseID: "",
        coursePath: "",
        courseImg: "",
        courseEnv360: "",
        courseEnv360Rot: "",
        coursePos: { x: 0, y: 0, z: 0 },
        courseRot: { x: 0, y: 0, z: 0 },
        courseAudioText: "",
        courseAudio: ""
      });
      setPathTopics([]);
      setPendingEnv360(null);
      setEditingIndex(null);
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
    setTimeout(() => {
      const anchor = document.getElementById("coursesList");
      if (anchor) {
        const yOffset = -80;
        const y = anchor.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 100);
  };

  const handleCancel = () => {
    setCourseData({
      courseID: "",
      courseTitle: "",
      courseDesc: "",
      courseRequiresCourseID: "",
      coursePath: "",
      courseImg: "",
      courseEnv360: "",
      courseEnv360Rot: "",
      coursePos: { x: 0, y: 0, z: 0 },
      courseRot: { x: 0, y: 0, z: 0 },
      courseAudioText: "",
      courseAudio: ""
    });
    setPathTopics([]);
    setPendingEnv360(null); // Ajoutez cette ligne
    setEditingIndex(null);
  };

  return (
    <div className="form-container p-4">
      {message && (
        <div
          className={`alert alert-${message.type === "success" ? "success" : "danger"
            } alert-dismissible fade show`}
          role="alert"
        >
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage(null)}
            aria-label="Close"
          ></button>
        </div>
      )}
      {/* Tableau des courses */}
      <a id="coursesList"></a>
      <h3 className="text-primary text-center">Liste des évaluations</h3>

      <div className="card shadow-sm mb-4 border border-primary" style={{ maxHeight: "320px", overflowY: "scroll" }}>
        <table className="table">
          <thead className="table-light">
            <tr>
              <th className="text-tertiary" style={{ width: "50px" }}>ID</th>
              <th className="text-tertiary" style={{ width: "200px" }}>Nom de l'Évaluation</th>
              <th className="text-tertiary">Description de l'Évaluation</th>
              <th className="text-tertiary" style={{ width: "100px" }}>Chemin</th>
              <th className="text-tertiary" style={{ width: "120px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(courses) && courses.length > 0 ? (
              courses.map((course, index) => (
                <tr key={course.courseID || index}>
                  <td>{course.courseID}</td>
                  <td className="fw-bold">{course.courseTitle}</td>
                  <td>{course.courseDesc}</td>
                  <td>{course.coursePath}</td>
                  <td alt="actions">
                    <button
                      className="btn btn-sm btn-outline-primary mx-1"
                      onClick={() => {
                        handleEdit(index);
                        setTimeout(() => {
                          const anchor = document.getElementById("courseEdit");
                          if (anchor) {
                            const yOffset = 15;
                            const y = anchor.getBoundingClientRect().top + window.pageYOffset + yOffset;
                            window.scrollTo({ top: y, behavior: "smooth" });
                          }
                        }, 100);
                      }}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger mx-1"
                      onClick={() => handleDelete(index)}
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>Aucune évaluation</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Formulaire d'ajout/édition */}
      <a id="courseEdit" style={{ marginTop: "-80px", paddingTop: "80px", display: "block" }}></a>
      <div className="form-container p-4">
        <Title
          text={
            editingIndex !== null
              ? `Modifier l'évaluation ${courseData.courseTitle ? ' "' + courseData.courseTitle + '"' : ''}`
              : "Créer une nouvelle évaluation"
          }
          className="text-center text-primary mb-4"
        />
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div alt="Titre de l'évaluation" className="col-md-4">
              <TextInput
                label="Titre de l'évaluation ℹ️"
                placeholder="Ex: Évaluation Dell M6600"
                value={courseData.courseTitle}
                onChange={(e) =>
                  handleChange("courseTitle", e.target.value)
                }
                className="mb-3"
              />
            </div>
            <div alt="Description de l'évaluation" className="col-md-4">
              <TextArea
                label="Description de l'évaluation ℹ️"
                placeholder="Ex: Maîtriser les opérations de maintenance"
                value={courseData.courseDesc}
                onChange={(e) =>
                  handleChange("courseDesc", e.target.value)
                }
                className="mb-4"
                rows={3}
              />
            </div>
            {/* <div alt="Texte audio" className="col-md-4">
              <TextArea
                label="Texte audio ℹ️"
                placeholder="Ex: Bienvenue dans la formation dédiée à la maintenance du Dell M6600. ..."
                value={courseData.courseAudioText}
                onChange={(e) =>
                  handleChange("courseAudioText", e.target.value)
                }
                className="mb-4"
                rows={3}
              />
            </div>
            <div className="col-md-4" alt="Environnement 360°" title="Environnement par défaut du sujet (optionnel),
              Si vide, l'utilisateur verra son environnement réel.">
              <label><i className="mdi mdi-panorama-horizontal" /> Image 360° (jpg)ℹ️</label>
              {!courseData.courseEnv360 ? (
                <input
                  type="file"
                  accept=".jpg"
                  className="form-control"
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) setPendingEnv360(file);
                  }}
                />
              ) : (
                <div className="mt-2">
                  <img
                    src={`/api/data/${folderPathFinal}/${courseData.courseEnv360}`}
                    alt="Vignette"
                    style={{ maxWidth: "60px", maxHeight: "60px", marginLeft: "10px", borderRadius: "4px", border: "1px solid #ccc" }}
                    onLoad={e => console.log('Vignette URL:', e.target.src)}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger me-2"
                    onClick={() => handleDeleteFile({
                      fileName: "env",
                      params: [courseData.courseID],
                      ext: ".jpg",
                      fieldName: "courseEnv360"
                    })}
                    title={`SUPPRIMER l'image de l'environnement (${folderPathFinal}/${courseData.courseEnv360})`}
                  >
                    <FaTrash />
                  </button>
                  <span className="text-success small fw-bold">Fichier actuel : {courseData.courseEnv360}</span>
                </div>
              )}
            </div> */}
            {/*  <div className="col-md-2" alt="RotEnv360x" title="Rotation de l'environnement 360° (en degrés).">
              <TextInput
                label="Rot° ℹ️"
                type="text"
                placeholder="0"
                value={courseData.courseEnv360Rot}
                onChange={e => {
                  const val = e.target.value;
                  if (/^-?[0-9]*[.,]?[0-9]*$/.test(val) || val === '') {
                    handleChange("courseEnv360Rot", val);
                    //handleChange("courseEnv360Rot", { ...courseData.courseEnv360Rot: val });
                  }
                }}
              />
            </div> */}
            <div alt="blank" className="col-md-2">
            </div>
            {/* <div alt="Fichier audio" className="col-md-4">
              <label className="form-label "><i className="mdi mdi-volume-high" /> Audio (ogg) ℹ️</label>
              <input
                type="file"
                accept=".ogg"
                className="form-control"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setFile(file);
                    handleUploadFile({ file, params: String([courseData.courseID]) || String([1]), fieldName: "courseAudio", suffix: "audio", defaultExt: ".mp3" });
                  }
                }}
              />
              {previewCourseAudioUrl ? (
                <audio
                  controls
                  src={previewCourseAudioUrl}
                  style={{ maxWidth: "calc(100% - 40px)", marginTop: '8px' }}
                  onLoadedData={e => console.log('Audio URL:', e.target.src)}
                >
                  Votre navigateur ne supporte pas l'audio.
                </audio>
              ) : (
                !!courseData.courseAudio && (
                  <div style={{ marginTop: '8px' }}>
                    <audio
                      controls
                      src={`/api/data/${folderPathFinal}/${courseData.courseAudio}`}
                      style={{ maxWidth: "calc(100% - 40px)" }}
                      onLoadedData={e => console.log('Audio URL:', e.target.src)}
                    >
                      Votre navigateur ne supporte pas l'audio.
                    </audio>
                    <button
                      type="button"
                      title="SUPPRIMER l'audio du cours"
                      className="btn btn-sm btn-outline-danger me-2"
                      onClick={() => handleDeleteFile({ fileName: "audio", params: String([courseData.courseID]), ext: "ogg", fieldName: "courseAudio" })}
                    >
                      <FaTrash />
                    </button>
                    <span className="text-success  small fw-bold">Fichier actuel : {courseData.courseAudio}</span>
                  </div>
                )
              )}
            </div> */}
          </div>

          <div className="row">
          </div>
          <div className="mt-2 p-4 border-primary rounded" style={{ borderStyle: 'dashed', borderWidth: '1px' }}>
            <h3 className="text-primary text-center">Contenu de l'évaluation</h3>
            <p className="col-md-6 text-center small mx-auto mb-4">
              Organisez votre évaluation à partir de la liste des questionnaires disponibles<br />
              Glissez-déposez les vers la colonne de chemin et ordonnez-les.
            </p>
            {/* Drag and Drop en ligne */}
            {/* Le key sur ce div force un démontage/remontage complet quand les topics
                disponibles changent, évitant tout conflit entre Sortable et le DOM React. */}
            <div key={sortableContainerKey} alt="Contenu DragNdrop" className="row">
              <div className="col-md-6">
                <label className="form-label">Questionnaires disponibles</label>
                <ul ref={leftRef} className="list-group border" style={{ height: "200px", overflowY: "auto" }}>
                  {availableTopics.map((topic, index) => (
                    <li key={topic.id || index} className="list-group-item" data-topicid={topic.id}>
                      <span className="badge bg-secondary me-2"># {topic.id}</span>
                      {topic.title}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Organisation de l'évaluation (glisser ici)
                </label>
                <ul ref={rightRef} className="list-group border border-primary" style={{ height: "200px", overflowY: "auto" }}>
                  {pathTopics.map((topicId, idx) => {
                    const topic = availableTopics.find(t => String(t.id) === String(topicId));
                    return (
                      <li key={`${topicId}-${idx}`} className="list-group-item d-flex justify-content-between align-items-center" data-topicid={topicId}>
                        <span>
                          <span className="badge bg-primary me-2"># {topicId}</span>
                          {topic ? (topic.title || `ID: ${topicId}`) : `ID: ${topicId}`}
                        </span>
                        <button
                          className="btn btn-sm btn-outline-danger mx-1"
                          onClick={() => handleRemoveTopicFromPath(topicId)}
                        >
                          <FaTrash />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* Boutons */}
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button
              text="Annuler"
              className="btn btn-lg btn-outline-secondary"
              onClick={() => {
                handleCancel();
                setTimeout(() => {
                  const anchor = document.getElementById("coursesList");
                  if (anchor) {
                    const yOffset = -80;
                    const y = anchor.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                  }
                }, 100);
              }}
              type="button"
            />
            <Button
              text={
                !courseData.courseTitle
                  ? "⚠️ Nom de l'évaluation manquant ⚠️"
                  : editingIndex !== null
                    ? `Enregistrer l'évaluation "${courseData.courseTitle}"`
                    : `Créer l'évaluation "${courseData.courseTitle}"`
              }
              type="submit"
              className="btn btn-lg btn-outline-primary"
              disabled={
                courseData.courseTitle.trim() === ""
              }
            />
          </div>
        </form>
      </div >
    </div >
  );
};

export default CoursesManage;

//Creators : Florian Cohen-Joly @3DDGAMES -- Under license